import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { observer } from 'mobx-react';
import { VerificationMessages, Typography } from 'radiance-ui';

import { FormField, FormInlineContainer } from 'jsx/components/formComponents';
import BaseFormObject from 'jsx/utils/form/baseMobxFormObject';
import { withValidation } from 'jsx/components/formHelper';
import {
  InformationRequestType,
  INFORMATION_REQUEST,
} from 'jsx/informationRequest/constants';

import INFORMATION_REQUEST_FIELDS from './informationRequestFields';
import { Button, Heading } from '../style';
import { Disclaimer, Instructions } from './style';

export interface FormDataType {
  firstname: string;
  lastname: string;
  address1: string;
  address2: string;
  city: string;
  zipcode: string;
  phone: string;
  email: string;
}

const InformationRequestForm = ({
  form,
  onSubmit,
  requestType,
}: {
  form: any;
  onSubmit: (values: FormDataType) => Promise<void>;
  requestType: InformationRequestType;
}): JSX.Element => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const history = useHistory();

  useEffect(() => {
    // eslint-disable-next-line no-param-reassign
    form.onSuccess = (): void => {
      setIsSubmitting(true);
      onSubmit(form.values())
        .catch((err) => setErrors(err.response.data.errors))
        .finally(() => {
          setIsSubmitting(false);
        });
    };
  }, [form, onSubmit]);

  const heading =
    requestType === INFORMATION_REQUEST.delete ? 'Delete' : 'Access';

  return (
    <form onSubmit={form.onSubmit}>
      <Typography.Caption>{heading} your information</Typography.Caption>
      <Heading>Confirm your identity</Heading>
      <Instructions>
        California residents: Confirm your identity below and our support team
        will email you with next steps. Your information is private and always
        will be.
      </Instructions>
      <FormInlineContainer>
        <FormField field={form.$('firstname')} type="text" />
        <FormField field={form.$('lastname')} type="text" />
      </FormInlineContainer>
      <FormField field={form.$('date_of_birth')} type="text" />
      <FormField field={form.$('address1')} type="text" />
      <FormField field={form.$('address2')} type="text" />
      <FormInlineContainer>
        <FormField field={form.$('city')} type="text" />
        <FormField field={form.$('zipcode')} type="text" />
      </FormInlineContainer>
      <FormField field={form.$('phone')} type="tel" />
      <FormField field={form.$('email')} type="email" />
      <Disclaimer>
        Don’t worry, we’ll only send you emails related to your request.
      </Disclaimer>
      <VerificationMessages messages={errors} />
      <Button
        type="submit"
        disabled={!form.isValid}
        data-test-id="submit"
        isLoading={isSubmitting}
      >
        Submit{' '}
        {requestType === INFORMATION_REQUEST.delete ? 'deletion' : 'access'}{' '}
        Request
      </Button>
      <Button
        buttonType="tertiary"
        onClick={(): void => history.goBack()}
        data-test-id="back"
      >
        Go Back
      </Button>
    </form>
  );
};

export default withValidation(
  observer(InformationRequestForm),
  INFORMATION_REQUEST_FIELDS,
  BaseFormObject,
);
